import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Calendar, CheckSquare, ChevronLeft, ChevronRight, Flag, GanttChart, GitBranch, LayoutGrid, LayoutList, Users, X } from 'lucide-react'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import { lengthError } from '../../lib/fieldLength'
import { formatDate, formatDateTime } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { FilterRow } from '../../components/filters/FilterRow'
import { TeamTaskDetailModal } from '../../components/TeamTaskDetailModal'
import {
  TaskFilterPill,
  dueRangeMatches,
} from '../../components/TaskFilterPill'
import { StickyTimelineScrollbar } from '../../components/StickyTimelineScrollbar'
import { smoothScrollLeftTo } from '../../lib/smooth-scroll'
import { StatusDot, isUnstartedStatus } from '../../components/StatusDot'
import { AvatarStack as SharedAvatarStack } from '../../components/AvatarStack'
import { CommentsCell } from '../../components/TaskComments'
import { useAuthStore } from '../../stores/authStore'
import {
  teammatesResponse,
  teamResponse,
  teamTaskResponse,
  workspaceListResponse,
} from '../../lib/types'
import { EmptyStateBanner } from '../../components/EmptyStateBanner'

// ─── Avatar helpers (mirrors the SpacePage palette so the same person
//     renders with the same colour everywhere). Kept local to this page
//     to avoid extracting AvatarStack into a shared module mid-iteration.

const AVATAR_PALETTE = [
  '#02367B', '#34D399', '#F87171', '#FBBF24', '#A78BFA',
  '#F472B6', '#0a2349', '#FB923C',
]

function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

function initialsFor(name) {
  const parts = String(name ?? '').trim().split(/\s+/)
  if (parts.length === 0 || parts[0] === '') return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function Avatar({ name, size = 24 }) {
  if (!name) {
    return (
      <span
        title="Unassigned"
        className="inline-flex items-center justify-center rounded-full border border-dashed border-[color:var(--color-rule-strong)] text-[color:var(--color-ink-subtle)]"
        style={{ height: size, width: size, fontSize: Math.round(size * 0.42) }}
      >
        —
      </span>
    )
  }
  return (
    <span
      title={name}
      className="inline-flex items-center justify-center rounded-full font-medium text-white"
      style={{
        height: size,
        width: size,
        fontSize: Math.round(size * 0.42),
        backgroundColor: avatarColor(name),
      }}
      aria-hidden="true"
    >
      {initialsFor(name)}
    </span>
  )
}

function AvatarStack({ members, size = 24 }) {
  if (!members || members.length === 0) return <Avatar name={null} size={size} />
  // Every assignee is drawn as its own avatar — no "+N" overflow chip.
  return (
    <div className="inline-flex items-center -space-x-2">
      {members.map((m, i) => (
        <span
          key={m.userId}
          className="ring-2 ring-[color:var(--color-card)] rounded-full"
          style={{ zIndex: members.length - i }}
        >
          <Avatar name={m.fullName} size={size} />
        </span>
      ))}
    </div>
  )
}



const PRIORITY_LABEL= {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Medium',
  low: 'Low',
}
const PRIORITY_COLOR= {
  urgent: 'var(--color-priority-urgent)',
  high: 'var(--color-priority-high)',
  normal: 'var(--color-priority-normal)',
  low: 'var(--color-priority-low)',
}

function fmtDate(iso){
  // Shared, timezone-aware DD/MM/YYYY formatter — renders in the tenant's
  // zone, not the viewer's browser zone.
  return formatDate(iso)
}

const DAY_MS = 24 * 60 * 60 * 1000

function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

/** Monday 00:00 of the current week, local time. Mirrors the backend's
 *  `startOfThisWeek` (ISO week, Monday start) so the "completed this
 *  week" view here lines up with the dashboard's "Completed This Week"
 *  card count, which is computed against the same boundary. */
function startOfThisWeek() {
  const x = startOfDay(new Date())
  const day = x.getDay() // 0=Sun .. 6=Sat
  const offset = day === 0 ? -6 : 1 - day
  x.setDate(x.getDate() + offset)
  return x
}

// Status palette — picks a colour by status name so the same status
// renders consistently across the Board column header and the Gantt
// section header. Matches the Space model's DEFAULT_TASK_STATUSES so
// most rows light up without configuration. Unknown names fall back
// to the muted grey reserved for "not started" categories.
const STATUS_TONE = {
  'TO DO': '#94a3b8',
  'IN PROGRESS': '#a855f7',
  'IN REVIEW': '#f59e0b',
  COMPLETE: '#10b981',
  COMPLETED: '#10b981',
  DONE: '#10b981',
  CLOSED: '#10b981',
  BACKLOG: '#94a3b8',
}

// Canonical left-to-right status order — TO DO → IN PROGRESS → (review) →
// COMPLETE — so the KPI cards, Board columns and List sections all read
// in the natural workflow direction regardless of what order the tasks
// happen to arrive in from the API. Statuses we don't recognise sort to
// the end (alphabetically) so custom workflows still render predictably.
const STATUS_ORDER = [
  'BACKLOG',
  'TO DO',
  'IN PROGRESS',
  'IN REVIEW',
  'COMPLETE',
  'COMPLETED',
  'DONE',
  'CLOSED',
]
function statusRank(name) {
  const i = STATUS_ORDER.indexOf(String(name ?? '').toUpperCase())
  return i === -1 ? STATUS_ORDER.length : i
}

// Built-in fallback ONLY — used when a status has no colour configured on its
// workflow (legacy/custom statuses the feed leaves uncoloured). The real colour
// comes from each task's own workflow via the backend `statusColor` field,
// resolved through the StatusColorContext resolver below.
function statusColorFor(name) {
  return STATUS_TONE[String(name ?? '').toUpperCase()] ?? '#94a3b8'
}

// Single source of truth for status → colour on this page. `forTask(task)` uses
// the task's OWN workflow colour (backend `statusColor`); `forLabel(label)` is
// the workflow-derived colour for an aggregate surface (summary card, board
// column, list section header). Provided by TeamTasksPage and consumed by the
// List / Board / Gantt sub-components + StatusPill — so the colour logic lives
// in exactly one place and never falls back to a global-by-label match for a
// task that could belong to a different workflow.
const StatusColorContext = createContext(null)
function useStatusColors() {
  return (
    useContext(StatusColorContext) ?? {
      forLabel: (label) => statusColorFor(label),
      forTask: (task) => task?.statusColor ?? statusColorFor(task?.status),
    }
  )
}

function StatusPill({ name }) {
  const color = useStatusColors().forLabel(name)
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium uppercase tracking-wider"
      style={{ color, backgroundColor: `${color}1f` }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {name}
    </span>
  )
}

function PriorityChip({ priority }) {
  if (!priority) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-subtle)]">
        <Flag size={12} />
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs"
      style={{ color: PRIORITY_COLOR[priority] }}
    >
      <Flag size={12} fill={PRIORITY_COLOR[priority]} strokeWidth={0} />
      {PRIORITY_LABEL[priority]}
    </span>
  )
}

export function TeamTasksPage() {
  // `user` no longer needs to flow through here for the canCreate
  // gate — that check moved to a `hasOperation('tasks.delegate')`
  // selector lower down. Other auth-state needs are scoped to
  // individual selectors at their use sites.
  const [view, setView] = useState('list')
  // Handle published by GanttView so the toolbar's "Today" button can scroll
  // its canvas. Null whenever the Gantt isn't mounted, so the button (which
  // only renders on that view) safely no-ops.
  const ganttTodayRef = useRef(null)
  // Filter state mirrors the Spaces page (List / Board / Gantt) so
  // the three pages read identically:
  //   • filterAssignees   — Set<userId | '__unassigned__'> (multi)
  //   • filterPriorities  — Set<'urgent'|'high'|'normal'|'low'|'none'> (multi)
  //   • filterDueRange    — one of DUE_RANGE_OPTIONS values (single)
  const [filterAssignees, setFilterAssignees] = useState(new Set())
  const [filterPriorities, setFilterPriorities] = useState(new Set())
  const [filterDueRange, setFilterDueRange] = useState('all')
  // `all` | `open` | `complete` — coarse completion-state filter used by
  // dashboard shortcut links (`?status=complete`). Independent of the
  // pill filter so a shortcut doesn't crowd out the user's own state.
  const [completionFilter, setCompletionFilter] = useState('all')
  // Free-text search across task name + space + list — sits alongside
  // the structured filter pills so a team leader can either narrow by
  // attribute OR jump straight to a known task by typing its name.
  const [search, setSearch] = useState('')
  // `all` | `week` — narrows COMPLETED tasks to those finished this ISO
  // week. Set by the dashboard's "Completed This Week" shortcut
  // (`?status=complete-week`) so the list matches that card's count.
  const [completedWithin, setCompletedWithin] = useState('all')
  // Team-Analytics drill-down ENTRY filters. Both arrive as deep-link
  // aliases (`?source=`, `?verification=`) from the Team Analytics KPI /
  // matrix cells and map onto the row fields the board now carries
  // (`creationSource`, `approvalStatus`). Cleared by "Clear all" like the
  // other alias-seeded filters.
  //   sourceFilter        — '' | manual | blueprint | ai | recurring
  //   verificationFilter  — '' | pending | approved | rejected
  const [sourceFilter, setSourceFilter] = useState('')
  const [verificationFilter, setVerificationFilter] = useState('')

  // ── Dashboard shortcut alias handling ────────────────────────────
  // Manager / Team-Leader dashboards link here with friendly aliases
  // (`?status=complete`, `?filter=overdue`, `?filter=today`).
  // Translate each into the page's existing filter state. The alias
  // STAYS in the URL now (QA-reported): the old mount-once handler
  // stripped it via `setSearchParams(..., { replace: true })`, so the
  // address bar lost the deep-link state the moment the page settled —
  // refresh / Back / copy-link all landed on the unfiltered page, and
  // the once-per-mount ref guard swallowed any card click that arrived
  // while this component was already mounted (the "dashboard card does
  // not navigate" symptom). Re-running on every search-param change is
  // safe: the mappings are idempotent setState calls, and a plain
  // `/app/team-tasks` navigation (no aliases) early-returns without
  // touching whatever filters the user has built up — exactly the
  // pre-existing behaviour after the old strip.
  //
  // `?action=new` (legacy "Add Task" dialog entry-point) is simply
  // ignored — the dialog is gone; a stale link just lands unfiltered.
  //
  // Known, accepted asymmetry: manually changing filters on-page does
  // NOT rewrite the URL — the alias is a deep-link ENTRY format, not a
  // full serialisation of filter state.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const statusAlias = searchParams.get('status')
    const filterAlias = searchParams.get('filter')
    const dueAlias = searchParams.get('due')
    // Team-Analytics drill-down aliases (single-dimension or combined with
    // the dashboard aliases above — each is an idempotent setState).
    const assigneeAlias = searchParams.get('assignee')
    const unassignedAlias = searchParams.get('unassigned')
    const sourceAlias = searchParams.get('source')
    const verificationAlias = searchParams.get('verification')
    if (
      !statusAlias &&
      !filterAlias &&
      !dueAlias &&
      !assigneeAlias &&
      !unassignedAlias &&
      !sourceAlias &&
      !verificationAlias
    )
      return

    if (statusAlias === 'complete') setCompletionFilter('complete')
    if (statusAlias === 'open') setCompletionFilter('open')
    // "Completed This Week" dashboard card — show only tasks completed
    // within the current ISO week so the list total equals the card.
    if (statusAlias === 'complete-week') {
      setCompletionFilter('complete')
      setCompletedWithin('week')
    }
    if (filterAlias === 'overdue') {
      setFilterDueRange('overdue')
      setCompletionFilter('open')
    }
    if (
      filterAlias === 'today' ||
      filterAlias === 'due-today' ||
      dueAlias === 'today'
    ) {
      setFilterDueRange('today')
    }
    if (filterAlias === 'complete' || filterAlias === 'completed') {
      setCompletionFilter('complete')
    }
    // Team-Analytics drill-down: focus one employee, the unassigned bucket,
    // a creation-source bucket, or a verification state. `unassigned` wins
    // over `assignee` (a KPI-strip "Unassigned" link never carries both).
    if (unassignedAlias) setFilterAssignees(new Set(['__unassigned__']))
    else if (assigneeAlias) setFilterAssignees(new Set([assigneeAlias]))
    if (sourceAlias) setSourceFilter(sourceAlias)
    if (verificationAlias) setVerificationFilter(verificationAlias)
  }, [searchParams])

  // Read-only task-details popup. Clicking a task adds `?task=<id>` to THIS
  // page instead of navigating to its Space — the Space route 403s for a
  // private Space the supervisor isn't a member of and bounced them to the
  // Dashboard (the reported bug). `?task=` doesn't collide with the status /
  // filter / due deep-link aliases above, which early-return when absent.
  const detailTaskId = searchParams.get('task')
  const closeDetail = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('task')
        return next
      },
      { replace: true },
    )

  const teamQuery = useQuery({
    queryKey: ['team', 'tasks'],
    queryFn: async () => {
      const res = await api.get('/team/tasks')
      return teamTaskResponse.parse(res.data)
    },
    // Overdue / Due Soon counts are time-dependent and due dates may be edited
    // elsewhere — poll once a minute so the summary cards stay live (the
    // overdue tally re-evaluates against the current clock on each refetch).
    refetchInterval: 60_000,
  })

  // Pull every supervised member once so we can resolve avatars + names
  // for the Assignees column without N round-trips per task.
  const teamMembersQuery = useQuery({
    queryKey: ['me', 'team'],
    queryFn: async () => {
      const res = await api.get('/me/team')
      return teamResponse.parse(res.data)
    },
  })
  const memberById = useMemo(() => {
    const map = new Map()
    for (const m of teamMembersQuery.data?.data ?? []) {
      // Derive the member's primary department from their placements.
      // A teammate can sit in multiple workspaces; we pick the FIRST
      // placement that carries a non-null departmentName so the Team
      // Tasks List view's "Department" column has a value to show.
      // If they're not in any department, this stays null and the
      // column renders an em-dash.
      const departmentName =
        (m.placements ?? []).find(
          (p) => typeof p.departmentName === 'string' && p.departmentName,
        )?.departmentName ?? null
      map.set(m.userId, {
        userId: m.userId,
        fullName: m.fullName,
        emailId: m.emailId,
        avatarUrl: m.avatarUrl ?? null,
        departmentName,
      })
    }
    return map
  }, [teamMembersQuery.data])

  const tasks = useMemo(() => teamQuery.data?.data ?? [], [teamQuery.data])

  // Status-colour resolver (provided to every child via StatusColorContext).
  //   • forTask  — the task's OWN workflow colour (backend `statusColor`), so
  //     two tasks with the same status label in different workflows keep their
  //     own colours. Falls back to forLabel when a task has no colour.
  //   • forLabel — for aggregate surfaces (summary cards, board columns, list
  //     section headers) that group by status NAME: prefer a colour a task of
  //     that label actually carries, then the default-workflow legend colour,
  //     then the built-in STATUS_TONE fallback.
  const statusColors = useMemo(() => {
    const byLabel = new Map() // UPPER label → colour
    for (const t of tasks) {
      const k = String(t.status ?? '').toUpperCase()
      if (t.statusColor && !byLabel.has(k)) byLabel.set(k, t.statusColor)
    }
    for (const s of teamQuery.data?.workflowStates ?? []) {
      const k = String(s.label ?? '').toUpperCase()
      if (s.color && !byLabel.has(k)) byLabel.set(k, s.color)
    }
    const forLabel = (label) =>
      byLabel.get(String(label ?? '').toUpperCase()) ?? statusColorFor(label)
    const forTask = (task) => task?.statusColor ?? forLabel(task?.status)
    return { forLabel, forTask }
  }, [tasks, teamQuery.data])

  // Status universe for the filter list and the List/Board groupings:
  // statuses present on the returned tasks UNIONED with the tenant's
  // configured default-workflow states from the feed payload. The
  // union is what keeps this page's taxonomy consistent with the
  // dashboard "Tasks by Status" donut, which pads zero-count entries
  // from the same workflow — without it a configured-but-empty status
  // (IN PROGRESS, 0 tasks) appeared in the donut but had no Board
  // column here (QA-reported). Task-derived spellings win on a
  // case-insensitive clash so existing groupings stay stable.
  const statusNames = useMemo(() => {
    const seen = new Map() // lower-cased name → display spelling
    for (const t of tasks) {
      const key = String(t.status).toLowerCase()
      if (!seen.has(key)) seen.set(key, t.status)
    }
    for (const s of teamQuery.data?.workflowStates ?? []) {
      if (!s.label) continue
      const key = s.label.toLowerCase()
      if (!seen.has(key)) seen.set(key, s.label)
    }
    // Sort into the canonical workflow order (TO DO → IN PROGRESS →
    // COMPLETE) so the summary cards, Board and List all line up. Drives
    // every status-ordered surface on this page.
    return Array.from(seen.values()).sort((a, b) => {
      const ra = statusRank(a)
      const rb = statusRank(b)
      if (ra !== rb) return ra - rb
      return String(a).localeCompare(String(b))
    })
  }, [tasks, teamQuery.data])

  // Overdue is TIME-dependent (a task flips the instant its due time passes).
  // The 60s refetch only recomputes when the data changes (TanStack
  // structuralSharing preserves the reference otherwise), so tick once a
  // minute to force the filter + Overdue summary card to re-evaluate against
  // the current clock. `nowTick` feeds the `filtered` deps below; `summary`
  // recomputes off `filtered`, so both stay live.
  const [nowTick, setNowTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      // Completion gate (dashboard shortcut). `open` = no completedAt;
      // `complete` = has completedAt. `all` is the default no-op.
      if (completionFilter === 'open' && t.completedAt) return false
      if (completionFilter === 'complete' && !t.completedAt) return false
      // Completed-this-week narrowing (dashboard "Completed This Week"
      // card). Only keep tasks whose completion fell in the current ISO
      // week, matching the card's count exactly.
      if (completedWithin === 'week') {
        if (!t.completedAt) return false
        if (new Date(t.completedAt) < startOfThisWeek()) return false
      }
      // Assignee — Set of selected userIds + the literal
      // '__unassigned__' for tasks with no assignees.
      if (filterAssignees.size > 0) {
        const assignees = t.assigneeIds ?? []
        const isUnassigned = assignees.length === 0
        let match = false
        if (filterAssignees.has('__unassigned__') && isUnassigned) {
          match = true
        }
        if (!match) {
          for (const id of assignees) {
            if (filterAssignees.has(String(id))) {
              match = true
              break
            }
          }
        }
        if (!match) return false
      }
      // Priority — multi-select. 'none' matches tasks with no
      // priority set.
      if (filterPriorities.size > 0) {
        const key = t.priority ?? 'none'
        if (!filterPriorities.has(key)) return false
      }
      // Creation-source bucket (Team-Analytics drill-down). Mirrors the
      // server's SOURCE_BUCKETS so the board's filtered rows agree with the
      // KPI count that linked here. A missing source is treated as 'manual'.
      if (sourceFilter) {
        const src = t.creationSource ?? 'manual'
        const inBucket =
          sourceFilter === 'ai'
            ? src === 'ai_sop' || src === 'ai_upload'
            : src === sourceFilter // manual | blueprint | recurring
        if (!inBucket) return false
      }
      // Verification state (Team-Analytics drill-down). Board rows carry
      // approvalStatus; 'none' is the un-submitted default.
      if (verificationFilter) {
        if ((t.approvalStatus ?? 'none') !== verificationFilter) return false
      }
      // Due date — single radio value. Pass `dueDateHasTime` so the Overdue
      // option is time-aware (a same-day timed task counts once its time
      // passes, not only after midnight).
      if (
        !dueRangeMatches(
          filterDueRange,
          t.dueDate,
          t.completedAt,
          t.dueDateHasTime,
        )
      )
        return false
      // Free-text search — matched against task name + space + list.
      if (search.trim()) {
        const q = search.trim().toLowerCase()
        const hay = `${t.name ?? ''} ${t.spaceName ?? ''} ${t.listName ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tasks,
    completionFilter,
    completedWithin,
    filterAssignees,
    filterPriorities,
    filterDueRange,
    sourceFilter,
    verificationFilter,
    search,
    // Re-evaluate time-based due filters (Overdue) once a minute.
    nowTick,
  ])

  const activeFilterCount =
    filterAssignees.size +
    filterPriorities.size +
    (filterDueRange !== 'all' ? 1 : 0) +
    (completionFilter !== 'all' ? 1 : 0) +
    (completedWithin !== 'all' ? 1 : 0) +
    (sourceFilter ? 1 : 0) +
    (verificationFilter ? 1 : 0)

  // "Clear all" must reset every filter dimension — including the
  // completion-state filters seeded from the dashboard deep-link
  // aliases — AND strip those aliases from the URL. Otherwise the
  // mount effect above re-seeds them on the next searchParams change
  // and the badge stays lit / the list stays empty until a full page
  // reload (DF_006 / bug 16566).
  function handleClearAllFilters() {
    setFilterAssignees(new Set())
    setFilterPriorities(new Set())
    setFilterDueRange('all')
    setCompletionFilter('all')
    setCompletedWithin('all')
    setSourceFilter('')
    setVerificationFilter('')
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    next.delete('filter')
    next.delete('due')
    next.delete('assignee')
    next.delete('unassigned')
    next.delete('source')
    next.delete('verification')
    setSearchParams(next, { replace: true })
  }

  // Status-distribution summary for the count cards above the list.
  // Keyed to the SAME `filtered` set the List/Board/Gantt views render,
  // so the cards always reconcile with what's on screen. One entry per
  // status (in the page's status order) plus a derived Overdue tally
  // (open tasks past their due date). Gives the leader an at-a-glance
  // read of task distribution before drilling into the groups.
  const summary = useMemo(() => {
    const byStatus = new Map(statusNames.map((s) => [s, 0]))
    let overdue = 0
    let total = 0
    for (const t of filtered) {
      // Every count card — status buckets, total AND overdue — counts EVERY
      // task, subtasks INCLUDED (each by its OWN status). This keeps these
      // figures in sync with the Manager Dashboard "Team Supervision" + Team
      // Overview KPIs, which also count subtasks (platform doctrine:
      // [[project_task_counts_include_subtasks]] — task-count cards include
      // subtasks). NB: the List sections + Board columns nest subtasks under
      // their parent card, so a status card can read higher than the top-level
      // rows shown below it — that's expected, not a mismatch.
      if (dueRangeMatches('overdue', t.dueDate, t.completedAt, t.dueDateHasTime))
        overdue += 1
      total += 1
      byStatus.set(t.status, (byStatus.get(t.status) ?? 0) + 1)
    }
    return {
      total,
      overdue,
      byStatus: statusNames.map((s) => ({
        status: s,
        count: byStatus.get(s) ?? 0,
      })),
    }
  }, [filtered, statusNames])

  // The shared CommentsCell needs the actor's id so it knows which
  // comments to expose a "Delete" affordance on.
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)

  return (
    <StatusColorContext.Provider value={statusColors}>
    <div>
      <Breadcrumb current="Team Tasks" homePath="/app" homeLabel="Dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            {/* Matches this route's sidebar icon (DF_029 / Mantis 22222). */}
            <Users
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Team Tasks
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            See tasks across your team.
          </p>
        </div>
        {/* "Add Task" button intentionally removed from this page. The
         *  dialog only sent an Inbox notification (no real Task row was
         *  created), which made the team-tasks list silently disagree
         *  with what people thought they had assigned. Task creation
         *  now goes through the regular Space → List → Task flow; this
         *  page stays read-only for tracking. */}
      </div>

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Task Name, Space or List"
        />
      </FilterRow>

      {/* Status summary cards — quick at-a-glance distribution of the
       *  team's tasks by state, plus a dedicated Overdue tally. Counts
       *  are computed from the same filtered set the views render, so a
       *  card never disagrees with the group totals below it. */}
      {!teamQuery.isLoading && !teamQuery.isError && tasks.length > 0 ? (
        <div className="flex flex-wrap gap-3 mb-4">
          {summary.byStatus.map(({ status, count }) => (
            <div
              key={status}
              className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-3 min-w-[140px] flex-1"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ background: statusColors.forLabel(status) }}
                  aria-hidden="true"
                />
                <span className="text-xs font-medium uppercase tracking-wide text-[color:var(--color-ink-muted)] truncate">
                  {status}
                </span>
              </div>
              <p className="mt-1 font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
                {count}
              </p>
            </div>
          ))}
          {/* Overdue — open tasks past their due date. Red accent so it
           *  reads as the "chase these first" signal the help text
           *  above calls out. */}
          <div className="rounded-xl border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-card)] px-4 py-3 min-w-[140px] flex-1">
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0 bg-[color:var(--color-priority-urgent)]"
                aria-hidden="true"
              />
              <span className="text-xs font-medium uppercase tracking-wide text-[color:var(--color-priority-urgent)] truncate">
                Overdue
              </span>
            </div>
            <p className="mt-1 font-display text-2xl tabular-nums leading-none text-[color:var(--color-priority-urgent)]">
              {summary.overdue}
            </p>
          </div>
        </div>
      ) : null}

      {/* View tabs — same boxed-button look the Space List page uses,
       *  so the active state reads consistently across both pages. */}
      <div className="flex flex-wrap items-center gap-1 mb-3">
        <ViewTab
          active={view === 'list'}
          icon={<LayoutList size={14} />}
          label="List"
          onClick={() => setView('list')}
        />
        <ViewTab
          active={view === 'board'}
          icon={<LayoutGrid size={14} />}
          label="Board"
          onClick={() => setView('board')}
        />
        <ViewTab
          active={view === 'gantt'}
          icon={<GanttChart size={14} />}
          label="Gantt"
          onClick={() => setView('gantt')}
        />
        <div className="ml-auto flex items-center gap-2">
          {/* Jump-to-today — Gantt only. Lives here rather than floating over
           *  the timeline header, where it kept covering a week or month label
           *  whatever offset it was given (QA). Calls the handle GanttView
           *  publishes; no-ops if the canvas isn't mounted yet. */}
          {view === 'gantt' ? (
            <button
              type="button"
              onClick={() => ganttTodayRef.current?.()}
              title="Jump to today"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[13px] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
            >
              <Calendar size={12} /> Today
            </button>
          ) : null}
          <TaskFilterPill
            members={Array.from(memberById.values())}
            filterAssignees={filterAssignees}
            setFilterAssignees={setFilterAssignees}
            filterPriorities={filterPriorities}
            setFilterPriorities={setFilterPriorities}
            filterDueRange={filterDueRange}
            setFilterDueRange={setFilterDueRange}
            activeCount={activeFilterCount}
            onClearAll={handleClearAllFilters}
            renderAvatar={(m) => <Avatar name={m?.fullName ?? null} size={20} />}
          />
        </div>
      </div>

      {/* Body */}
      {teamQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading tasks…</p>
      ) : teamQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(teamQuery.error).message}
        </p>
      ) : filtered.length === 0 ? (
        // Shared empty-state banner — kept at the parent so List / Board /
        // Gantt all show the same surface instead of three different
        // "no rows" treatments. The icon swaps to match the active view
        // (LayoutList / LayoutGrid / GanttChart) so the banner reads as
        // part of the view the user picked rather than a generic page
        // fallback.
        <EmptyView view={view} hasAnyTasks={tasks.length > 0} />
      ) : view === 'list' ? (
        <ListView
          tasks={filtered}
          statusNames={statusNames}
          memberById={memberById}
          currentUserId={currentUserId}
        />
      ) : view === 'board' ? (
        <BoardView
          tasks={filtered}
          statusNames={statusNames}
          memberById={memberById}
        />
      ) : (
        <GanttView tasks={filtered} todayHandleRef={ganttTodayRef} />
      )}

      {/* CreateTeamTaskDialog mount removed alongside the Add Task
       *  button — the dialog only created an Inbox notification, never
       *  a real Task row. Component left in this file so it's easy to
       *  re-wire later if/when the "assign and create" backend flow
       *  catches up to the dialog's UX promise. */}

      {/* Read-only task-details popup — opens in place from `?task=<id>` so a
       *  supervisor can view a task in a private Space they aren't a member of
       *  without being bounced to the Dashboard. */}
      <TeamTaskDetailModal taskId={detailTaskId} onClose={closeDetail} />
    </div>
    </StatusColorContext.Provider>
  )
}

// ---------- Subcomponents ----------

/**
 * Shared empty-state banner shown by List / Board / Gantt when there
 * are no rows to render. Two variants:
 *
 *   - `hasAnyTasks=false` — the team genuinely has no tasks. The
 *     banner explains why this page might stay empty (delegated
 *     "Assign Task" briefs only create Inbox notifications, not
 *     Task rows — real rows only appear once a teammate creates the
 *     task inside a shared workspace).
 *   - `hasAnyTasks=true`  — tasks exist but the active filters knock
 *     them all out. Shorter banner that prompts toward clearing
 *     filters instead of repeating the "how it works" copy.
 *
 * The icon swaps with the active view so the banner reads as part of
 * the chosen view rather than a generic page fallback.
 */
function EmptyView({ view, hasAnyTasks }) {
  const Icon =
    view === 'board' ? LayoutGrid : view === 'gantt' ? GanttChart : LayoutList
  const viewLabel =
    view === 'board' ? 'Board' : view === 'gantt' ? 'Gantt' : 'List'
  return (
    <EmptyStateBanner
      icon={Icon}
      title={
        hasAnyTasks
          ? `No tasks match your filters in ${viewLabel} view`
          : `Your team's ${viewLabel} view is empty`
      }
      body={
        hasAnyTasks
          ? 'Try clearing a filter or widening the date range to see more tasks.'
          : 'Tasks created in workspaces you share with your team will appear here automatically — across List, Board, and Gantt.'
      }
      tone={hasAnyTasks ? 'amber' : 'sky'}
    />
  )
}

function ViewTab({
  active,
  icon,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm cursor-pointer ' +
        (active
          ? 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-strong)] font-medium'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
      }
    >
      {icon}
      {label}
    </button>
  )
}

// Minimum table width for the List view. Drives the sticky-footer
// horizontal scrollbar — when the viewport is narrower than this,
// the user can scrub the bottom bar to scan the seven columns
// (Name / Department / Assignee / Start Date / Due Date / Priority
// / Created By) — same layout as the Task Monitor List.
const LIST_MIN_WIDTH = 1200

function ListView({ tasks, statusNames, memberById, currentUserId }) {
  const statusColors = useStatusColors()
  // Sticky-footer horizontal scrollbar — same pattern as the Gantt.
  // Wraps every status section in a shared scroll container so the
  // bar at the viewport bottom mirrors one consistent scroll position.
  const listRef = useRef(null)

  // Subtask grouping — same logic as the Task Monitor List view.
  // Top-level tasks contribute to status sections; subtasks render
  // nested under their parent (chevron-expand pattern).
  const subtasksByParent = useMemo(() => {
    const map = new Map()
    for (const t of tasks) {
      if (!t.parentTaskId) continue
      const key = String(t.parentTaskId)
      const arr = map.get(key) ?? []
      arr.push(t)
      map.set(key, arr)
    }
    // Order each parent's subtasks by creation time (oldest first), then
    // id as a stable tie-break — so they read in the order they were
    // created, matching the Space List view. The /team/tasks feed arrives
    // sorted by `updatedAt desc`, which otherwise scrambled subtask order.
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          new Date(a.createdAt ?? 0) - new Date(b.createdAt ?? 0) ||
          String(a.id).localeCompare(String(b.id)),
      )
    }
    return map
  }, [tasks])

  const groups = useMemo(() => {
    const map = new Map()
    for (const s of statusNames) map.set(s, [])
    for (const t of tasks) {
      if (t.parentTaskId) continue
      if (!map.has(t.status)) map.set(t.status, [])
      map.get(t.status)?.push(t)
    }
    return Array.from(map.entries())
  }, [tasks, statusNames])

  // Per-parent expand/collapse for subtasks.
  const [expandedParents, setExpandedParents] = useState(() => new Set())
  function toggleExpanded(taskId) {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  // Status-section collapse — matches the Task Monitor List view.
  const [collapsedStatuses, setCollapsedStatuses] = useState(() => new Set())
  function toggleStatus(name) {
    setCollapsedStatuses((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  // Resolvers — pull the per-task Department / primary Assignee /
  // Created By values out of the cached `memberById` lookup so the
  // 7 columns mirror the Task Monitor exactly.
  const primaryDepartmentName = (t) => {
    const id = (t.assigneeIds ?? [])[0]
    if (!id) return null
    return memberById.get(id)?.departmentName ?? null
  }
  const creatorName = (t) =>
    memberById.get(t.createdById)?.fullName ?? null

  // ListView no longer renders its own empty-state — the parent page
  // shows `EmptyView` when `filtered.length === 0` so List / Board /
  // Gantt all share one banner. ListView is only mounted when there's
  // at least one row to paint.

  // Common row renderer — `<tr>` shape that mirrors the Task Monitor.
  // `depth=0` = top-level parent row (chevron, status dot, name,
  // sub-count badge). `depth=1` = nested subtask (indented inside the
  // Task column, slightly muted background).
  function renderRow(t, depth = 0) {
    const subs = subtasksByParent.get(String(t.id)) ?? []
    // Show the expand chevron + subtask-count badge at ANY depth, not
    // just top-level — a subtask can have its own subtasks (3+ level
    // hierarchies), and gating on `depth === 0` hid the chevron so those
    // grandchildren could never be revealed. Pairs with the recursive
    // renderTaskTree below.
    const hasSubs = subs.length > 0
    const expanded = expandedParents.has(t.id)
    const dot = statusColors.forTask(t)
    return (
      <tr
        key={t.id}
        className={
          'border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-card-hover)] ' +
          (depth === 1 ? 'bg-[color:var(--color-canvas)]/40' : '')
        }
      >
        <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
          <div
            className="flex items-center gap-2 min-w-0"
            style={{ paddingLeft: depth * 24 }}
          >
            {hasSubs ? (
              <button
                type="button"
                onClick={() => toggleExpanded(t.id)}
                aria-expanded={expanded}
                title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
                className="p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]"
              >
                <ChevronRight
                  size={14}
                  className={
                    'shrink-0 transition-transform ' +
                    (expanded ? 'rotate-90' : '')
                  }
                />
              </button>
            ) : (
              <span className="w-4 shrink-0" aria-hidden="true" />
            )}
            <StatusDot
              color={dot}
              unstarted={isUnstartedStatus(null, t.status)}
              size={14}
            />
            <Link
              // Open the task's detail/overview panel directly (via the
              // `?task=` deep-link the Space page honours) rather than the
              // old `?focusTask=`, which only scrolled the List view to the
              // row. Clicking a task now lands on that task's detail, not
              // the list it lives in (DF_19 / bug 16553).
              to={`/app/team-tasks?task=${t.id}`}
              className={
                'truncate font-normal hover:underline cursor-pointer ' +
                (depth === 0
                  ? 'text-[color:var(--color-ink-strong)]'
                  : 'text-[color:var(--color-ink)]')
              }
            >
              {t.name}
            </Link>
            {/* Overdue flag — open task whose due date has passed. Passes
             *  `dueDateHasTime` so this is the SAME time-aware predicate the
             *  Overdue summary card counts with (see `summary` above): a task
             *  due EARLIER TODAY with a specific time is overdue the instant
             *  that time passes, so a badged row always reconciles with the
             *  card count. Omitting the flag silently regressed this — a timed
             *  same-day task was counted in the card yet shown WITHOUT the
             *  badge (DF_017 / bug 13332). Keep the 4th arg. */}
            {dueRangeMatches(
              'overdue',
              t.dueDate,
              t.completedAt,
              t.dueDateHasTime,
            ) ? (
              <span className="ml-1.5 shrink-0 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[color:var(--color-priority-urgent)]/10 text-[color:var(--color-priority-urgent)]">
                Overdue
              </span>
            ) : null}
            {hasSubs ? (
              <span
                className="ml-1 text-[10px] text-[color:var(--color-ink-muted)] inline-flex items-center gap-0.5"
                title={`${subs.length} subtask${
                  subs.length === 1 ? '' : 's'
                }`}
              >
                <GitBranch size={11} />
                {subs.length}
              </span>
            ) : null}
          </div>
        </td>
        {/* Space / Project / List — where the task lives. Shows the full path
         *  (project segment omitted when the List sits directly under the
         *  Space). Truncates on the fixed column width; full path on hover. */}
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {(() => {
            const path = [t.spaceName, t.projectName, t.listName]
              .filter(Boolean)
              .join(' / ')
            return (
              <span className="block truncate" title={path || undefined}>
                {path || '—'}
              </span>
            )
          })()}
        </td>
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {primaryDepartmentName(t) ?? '—'}
        </td>
        <td className="px-4 py-3">
          {(() => {
            // Expand the task's assigneeIds to full member rows via
            // the cached `memberById` lookup, then hand them to the
            // shared AvatarStack so multi-assignee tasks read at a
            // glance (matching the Space List view). Falls back to
            // "Unassigned" when the array is empty.
            const ids = t.assigneeIds ?? []
            const people = ids
              .map((id) => memberById.get(id))
              .filter(Boolean)
            return people.length > 0 ? (
              <SharedAvatarStack people={people} size={24} />
            ) : (
              <span className="text-[color:var(--color-ink-muted)]">
                Unassigned
              </span>
            )
          })()}
        </td>
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {t.startDate ? fmtDate(t.startDate) : '—'}
        </td>
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {t.dueDate ? fmtDate(t.dueDate) : '—'}
        </td>
        <td className="px-4 py-3">
          <PriorityChip priority={t.priority ?? null} />
        </td>
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {/* Shared comments popover — count + click-to-open thread,
           *  identical to the Space List view's cell. */}
          <CommentsCell
            taskId={t.id}
            currentUserId={currentUserId}
            initialCount={t.commentCount ?? 0}
            zeroLabel="—"
          />
        </td>
        <td className="px-4 py-3 text-[color:var(--color-ink)]">
          {creatorName(t) ?? '—'}
        </td>
      </tr>
    )
  }

  // Render a task and, when expanded, its ENTIRE subtask subtree —
  // recursing to every depth so grandchildren (a subtask's own subtasks)
  // show too, matching the Space List view. The previous render only
  // emitted one level (parent → direct subtasks), so deeper subtasks were
  // hidden even though they exist in the data and the count cards include
  // them.
  function renderTaskTree(t, depth = 0) {
    const out = [renderRow(t, depth)]
    const subs = subtasksByParent.get(String(t.id)) ?? []
    if (expandedParents.has(t.id) && subs.length > 0) {
      for (const sub of subs) out.push(...renderTaskTree(sub, depth + 1))
    }
    return out
  }

  return (
    <>
      <div
        ref={listRef}
        className="space-y-4 overflow-x-auto view-hscroll-sticky"
      >
        {groups.map(([status, rows]) => {
          // Render EVERY status section — including empty ones — so the
          // List view mirrors the Board (which shows all columns with a
          // "No tasks." placeholder). Empty sections get the placeholder
          // body below instead of being dropped.
          const isCollapsed = collapsedStatuses.has(status)
          const color = statusColors.forLabel(status)
          return (
            <section
              key={status}
              style={{ minWidth: LIST_MIN_WIDTH }}
              className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleStatus(status)}
                aria-expanded={!isCollapsed}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-left cursor-pointer hover:bg-[color:var(--color-card-hover)]"
              >
                {isCollapsed ? (
                  <ChevronRight
                    size={14}
                    className="text-[color:var(--color-ink-muted)]"
                  />
                ) : (
                  <ChevronRight
                    size={14}
                    className="text-[color:var(--color-ink-muted)] rotate-90"
                  />
                )}
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <span
                  className="text-sm font-medium uppercase tracking-wider"
                  style={{ color }}
                >
                  {status}
                </span>
                <span className="text-sm text-[color:var(--color-ink-muted)]">
                  ({rows.length})
                </span>
              </button>
              {isCollapsed ? null : rows.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-[color:var(--color-ink-muted)]">
                  No tasks.
                </div>
              ) : (
                <div>
                  {/* `table-fixed` + explicit `<colgroup>` widths so
                   *  every status section sizes its columns
                   *  identically. Mirrors the same fix on the Task
                   *  Monitor page so List view reads consistently. The
                   *  Name column is intentionally the widest so long
                   *  task/subtask names aren't truncated. No per-section
                   *  `overflow-x-auto` here: `min-w-[1200px]` lets the
                   *  table overflow the OUTER scroll container, which owns
                   *  the single sticky horizontal scrollbar — otherwise
                   *  every status section drew its own native bar and the
                   *  sticky one had nothing to scroll. */}
                  <table className="w-full min-w-[1200px] table-fixed text-left text-sm">
                    <colgroup>
                      {/* Name */}
                      <col style={{ width: '20%' }} />
                      {/* Space — added so team members see each task's Space,
                          matching the "Search by … Space" affordance (QA). */}
                      <col style={{ width: '13%' }} />
                      {/* Department */}
                      <col style={{ width: '12%' }} />
                      {/* Assignee */}
                      <col style={{ width: '12%' }} />
                      {/* Start Date */}
                      <col style={{ width: '9%' }} />
                      {/* Due Date */}
                      <col style={{ width: '9%' }} />
                      {/* Priority */}
                      <col style={{ width: '7%' }} />
                      {/* Comments */}
                      <col style={{ width: '8%' }} />
                      {/* Created By */}
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead className="bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
                      <tr className="border-b-2 border-[color:var(--color-rule)]">
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Task Name
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)] whitespace-nowrap">
                          Space / Project / List
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Department
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Assignee
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Start Date
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Due Date
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Priority
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Comments
                        </th>
                        <th className="px-4 py-3 font-medium text-sm text-[color:var(--color-ink-muted)]">
                          Created By
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.flatMap((t) => renderTaskTree(t, 0))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )
        })}
      </div>
      {/* Sticky horizontal scrollbar pinned to the viewport bottom —
       *  identical pattern to the Gantt views. */}
      <StickyTimelineScrollbar
        timelineRef={listRef}
        timelineWidth={LIST_MIN_WIDTH}
      />
    </>
  )
}

/** Kanban-style Board grouped by status. Mirrors the Space List page's
 *  Board: column header carries a coloured StatusPill + count, cards
 *  render task name + Space/List context + assignee avatar stack +
 *  priority chip. Reordering by drag-and-drop is intentionally NOT
 *  wired here — Team Tasks is a read-mostly cross-Space view; status
 *  changes happen on the originating Space. */
function BoardView({ tasks, statusNames, memberById }) {
  // Group subtasks under their parent so the column only shows
  // top-level cards. Subtasks render nested inside the parent card
  // with an expand chevron — same pattern as the List view.
  const subtasksByParent = useMemo(() => {
    const map = new Map()
    for (const t of tasks) {
      if (!t.parentTaskId) continue
      const key = String(t.parentTaskId)
      const arr = map.get(key) ?? []
      arr.push(t)
      map.set(key, arr)
    }
    // Created-order (oldest first) so subtasks read like the Space view.
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          new Date(a.createdAt ?? 0) - new Date(b.createdAt ?? 0) ||
          String(a.id).localeCompare(String(b.id)),
      )
    }
    return map
  }, [tasks])

  const [expandedParents, setExpandedParents] = useState(() => new Set())
  function toggleExpanded(taskId) {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  // Compact card body — shared between the parent card (Link) and
  // each nested subtask row. `depth=1` indents subtask cards so the
  // parent/child relationship reads at a glance.
  function CardBody({ t, depth = 0 }) {
    const assignees = (t.assigneeIds ?? [])
      .map((id) => memberById.get(id))
      .filter(Boolean)
    return (
      <>
        <p className="text-sm font-normal text-[color:var(--color-ink-strong)] truncate">
          {t.name}
        </p>
        <p className="text-xs text-[color:var(--color-ink-muted)] truncate mt-0.5">
          {t.spaceName} · {t.listName}
        </p>
        {t.dueDate ? (
          <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)] inline-flex items-center gap-1">
            <Calendar size={12} />
            {fmtDate(t.dueDate)}
          </p>
        ) : null}
        <div className="mt-2 flex items-center justify-between">
          <AvatarStack members={assignees} size={depth === 0 ? 22 : 18} />
          <PriorityChip priority={t.priority ?? null} />
        </div>
      </>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
      {statusNames.map((status) => {
        // Only TOP-LEVEL tasks contribute to the column count; subtasks
        // render inside their parent's card. Filter accordingly.
        const rows = tasks.filter(
          (t) => t.status === status && !t.parentTaskId,
        )
        return (
          <div
            key={status}
            className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm overflow-hidden"
          >
            <div className="px-4 py-2.5 bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] flex items-center gap-2">
              <StatusPill name={status} />
              <span className="ml-auto text-sm text-[color:var(--color-ink-muted)]">
                {rows.length}
              </span>
            </div>
            <div className="p-3 space-y-2 min-h-[80px]">
              {rows.length === 0 ? (
                <p className="text-sm text-[color:var(--color-ink-muted)] py-4 text-center">
                  No tasks.
                </p>
              ) : (
                rows.map((t) => {
                  const subs = subtasksByParent.get(String(t.id)) ?? []
                  const hasSubs = subs.length > 0
                  const expanded = expandedParents.has(t.id)
                  return (
                    <div key={t.id}>
                      <Link
                        // Open the task's detail panel directly, matching the
                        // List view (DF_19 / bug 16553).
                        to={`/app/team-tasks?task=${t.id}`}
                        className="block rounded-md border border-[color:var(--color-rule)] p-3 bg-[color:var(--color-card)] hover:shadow-sm transition-shadow cursor-pointer"
                      >
                        <CardBody t={t} depth={0} />
                        {hasSubs ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              // Stop the parent Link from navigating
                              // when the user just wants to expand.
                              e.preventDefault()
                              e.stopPropagation()
                              toggleExpanded(t.id)
                            }}
                            aria-expanded={expanded}
                            title={expanded ? 'Hide subtasks' : 'Show subtasks'}
                            className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
                          >
                            <ChevronRight
                              size={12}
                              className={
                                'transition-transform ' +
                                (expanded ? 'rotate-90' : '')
                              }
                            />
                            <span>
                              {subs.length}{' '}
                              {subs.length === 1 ? 'subtask' : 'subtasks'}
                            </span>
                          </button>
                        ) : null}
                      </Link>
                      {/* Subtask cards rendered nested below the
                       *  parent. Slightly muted background + left
                       *  border so the visual hierarchy reads. */}
                      {hasSubs && expanded
                        ? subs.map((sub) => (
                            <Link
                              key={sub.id}
                              // Subtask detail panel (DF_19 / bug 16553).
                              to={`/app/team-tasks?task=${sub.id}`}
                              className="block rounded-md border border-[color:var(--color-rule)] border-l-2 border-l-[color:var(--color-accent)] p-2.5 mt-1.5 ml-4 bg-[color:var(--color-canvas)] hover:shadow-sm transition-shadow cursor-pointer"
                            >
                              <CardBody t={sub} depth={1} />
                            </Link>
                          ))
                        : null}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Gantt date helpers ─────────────────────────────────────────────

/** ISO week number (Mon-leading). Used to label week strips above the
 *  day columns — e.g. "W19 · May 10–16". */
function isoWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d - yearStart) / DAY_MS + 1) / 7)
}

/** Two-pane Gantt — fixed Name + Due Date columns on the left, scrolling
 *  timeline on the right. Header carries a week strip (week number plus
 *  date range) above day cells; weekends are hatched, today gets an
 *  accent badge in the day cell and a vertical guide through the body.
 *  Tasks group by Space · List, each list collapsible. Inspired by the
 *  flat dark Gantt the user shared as reference but recoloured for the
 *  light theme. */
function GanttView({
  tasks,
  /** Ref the parent owns so the view-tabs toolbar can trigger "jump to
   *  today". The scroll logic closes over this component's pane ref + date
   *  range, so it can't live in the parent — we publish the current closure
   *  into `todayHandleRef.current` and the toolbar calls it. The button used
   *  to float over the timeline header and kept covering a week / month
   *  label (QA); the toolbar has no such collision. Mirrors the Space Gantt. */
  todayHandleRef,
}) {
  const statusColors = useStatusColors()
  const dueTasks = tasks.filter((t) => t.dueDate)
  // Tasks with no due date can't be placed on the timeline. Surface a
  // count so they aren't dropped silently — the summary counters above
  // the view count every task, so without this notice the Gantt looked
  // like it lost 11 of 12 tasks (DF_007 / bug 16567).
  const hiddenCount = tasks.length - dueTasks.length
  // Ref to the timeline scroll container — the StickyTimelineScrollbar
  // mounts a fixed-position scrollbar at the viewport bottom and
  // mirrors scrollLeft both ways. Matches the Spaces page Gantt's
  // scroll behaviour exactly.
  const timelineRef = useRef(null)

  // ── Group tasks by Space · List so each list reads as its own row
  //    cluster, matching the reference's "List ▶ tasks" tree. */
  //  Each group separates TOP-LEVEL tasks from subtasks so the Gantt
  //  can render an expand chevron per parent (matching the List + Board
  //  views). `tasks` here keeps the flat sequence for headers / counts
  //  but `parents` + `subtasksByParent` drive the actual row layout.
  const groups = useMemo(() => {
    const map = new Map()
    for (const t of dueTasks) {
      const key = `${t.spaceId}::${t.listId}`
      if (!map.has(key)) {
        map.set(key, {
          key,
          spaceId: t.spaceId,
          listId: t.listId,
          spaceName: t.spaceName,
          listName: t.listName,
          tasks: [],
          parents: [],
          subtasksByParent: new Map(),
        })
      }
      const group = map.get(key)
      group.tasks.push(t)
      if (t.parentTaskId) {
        const parentKey = String(t.parentTaskId)
        const arr = group.subtasksByParent.get(parentKey) ?? []
        arr.push(t)
        group.subtasksByParent.set(parentKey, arr)
      } else {
        group.parents.push(t)
      }
    }
    return Array.from(map.values())
  }, [dueTasks])

  // Per-parent expand/collapse state for Gantt subtasks. Default-
  // collapsed so a long list of parents doesn't dump every subtask
  // into the timeline at once.
  const [expandedParents, setExpandedParents] = useState(() => new Set())
  const toggleParent = (taskId) =>
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })

  // Track collapsed group keys. Default-expanded; clicking the chevron
  // hides that group's task rows but keeps the header visible.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // ── Infinite-scroll bounds — same pattern as the Spaces page Gantt.
  //
  // The visible canvas starts at ±1 year around today and grows by
  // another year whenever the user scrolls within EDGE_DAYS of either
  // edge. Tasks outside the active window still expand it (their
  // due-date pulls the range out). We never truncate — once the
  // canvas grows, it stays grown for the page's lifetime.
  const PAST_EXTEND = 365
  const FUTURE_EXTEND = 365
  const EDGE_DAYS = 14
  const [pastDays, setPastDays] = useState(365)
  const [futureDays, setFutureDays] = useState(365)
  const extendingRef = useRef(false)

  // Viewport state — drives the ClickUp-style off-screen indicator
  // chevrons. When a task's bar sits entirely to the left or right
  // of the visible canvas, a button appears at the viewport edge so
  // the user can jump to the bar without scrubbing the scrollbar.
  // `clientWidth = 0` until first measure, which we guard against
  // below so initial paint doesn't show a chevron on every row.
  const [viewport, setViewport] = useState({ scrollLeft: 0, clientWidth: 0 })
  const viewportRafRef = useRef(null)

  // Initial measure + ResizeObserver — keeps `clientWidth` correct
  // as the sidebar collapses, the window resizes, or the parent
  // re-flows.
  useEffect(() => {
    const el = timelineRef.current
    if (!el) return
    const measure = () => {
      setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const today = startOfDay(new Date())

  // Range = min/max due-date plus an infinite-scroll floor/ceiling.
  let min = today
  let max = today
  for (const t of dueTasks) {
    const d = startOfDay(new Date(t.dueDate))
    if (d < min) min = d
    if (d > max) max = d
  }
  // Apply the past/future bounds derived from the infinite-scroll
  // state. If the active task window is wider than the bounds, it
  // wins; otherwise the bounds extend the canvas to ±pastDays /
  // futureDays around today.
  const pastFloor = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - pastDays,
  )
  const futureCeil = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + futureDays,
  )
  if (min > pastFloor) min = pastFloor
  if (max < futureCeil) max = futureCeil
  // Snap min back to Monday and max forward to Sunday with a 7-day pad
  // so the week strip lands on whole columns.
  while (min.getDay() !== 1) min = new Date(min.getTime() - DAY_MS)
  while (max.getDay() !== 0) max = new Date(max.getTime() + DAY_MS)

  // When the past edge extends, `min` shifts earlier and every existing
  // bar moves further right inside the wider canvas. Compensate
  // `scrollLeft` by the pixel delta right before paint so the visible
  // bar stays put — without this, the user appears to teleport
  // every time the canvas grows. Same pattern as the Spaces Gantt.
  const prevMinRef = useRef(null)
  useLayoutEffect(() => {
    const prev = prevMinRef.current
    if (prev && prev.getTime() !== min.getTime()) {
      const el = timelineRef.current
      if (el) {
        const shiftDays = Math.round(
          (prev.getTime() - min.getTime()) / DAY_MS,
        )
        // DAY_W is defined below as 56 — hard-coded here to avoid
        // hoisting it above the hook section.
        if (shiftDays > 0) el.scrollLeft += shiftDays * 56
      }
    }
    prevMinRef.current = min
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min.getTime()])

  // Open the Gantt centred on TODAY — once, as soon as the timeline has a
  // measured width. Without this the canvas rendered scrolled to its far-left
  // (past) edge, so the view never defaulted to today AND the first "Today"
  // click had to scroll all the way from the left edge, tripping the
  // past-extend mid-tween and landing short (hence the reported two-click).
  // The today-x maths is inlined from `xForToday()` (which is declared below
  // the early return); DAY_W is hard-coded to 56 here, as the min-shift effect
  // above does, to avoid referencing a `const` declared later.
  const didInitialScrollRef = useRef(false)
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return
    const el = timelineRef.current
    if (!el || el.clientWidth === 0) return
    const todayX = ((today.getTime() - min.getTime()) / DAY_MS) * 56
    el.scrollLeft = todayX + 56 / 2 - el.clientWidth / 2
    didInitialScrollRef.current = true
  })

  // Scroll handler — detects EDGE_DAYS-near-the-edge and bumps the
  // active window. Guarded by `extendingRef` so a single edge-scroll
  // doesn't fire setPastDays/setFutureDays repeatedly. Also throttles
  // a viewport-state update via rAF so the off-screen chevrons stay
  // pinned to the current viewport edge without thrashing renders.
  const onTimelineScroll = (e) => {
    const el = e.currentTarget
    // rAF-throttled viewport state update for the off-screen chevrons.
    if (viewportRafRef.current == null) {
      viewportRafRef.current = requestAnimationFrame(() => {
        viewportRafRef.current = null
        setViewport({ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth })
      })
    }
    if (extendingRef.current) return
    const threshold = 56 * EDGE_DAYS // 56 = DAY_W
    const remainingRight = el.scrollWidth - el.scrollLeft - el.clientWidth
    if (el.scrollLeft < threshold) {
      extendingRef.current = true
      setPastDays((p) => p + PAST_EXTEND)
      requestAnimationFrame(() => {
        extendingRef.current = false
      })
      return
    }
    if (remainingRight < threshold) {
      extendingRef.current = true
      setFutureDays((f) => f + FUTURE_EXTEND)
      requestAnimationFrame(() => {
        extendingRef.current = false
      })
    }
  }

  const days = []
  for (let d = new Date(min); d <= max; d = new Date(d.getTime() + DAY_MS)) {
    days.push(new Date(d))
  }

  // Build week strips — Mon → Sun chunks of `days`, each carrying the
  // ISO week number and a "DD/MM–DD/MM" label so the user can place
  // themselves at a glance. Canonical date format (DD/MM/YYYY) — the
  // surrounding Gantt range implies the year, so we drop it here for
  // a tighter banner. Matches `lib/format.js`.
  const weeks = []
  for (let i = 0; i < days.length; i += 7) {
    const start = days[i]
    const end = days[Math.min(i + 6, days.length - 1)]
    const fmtDM = (d) =>
      `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
    weeks.push({
      key: `w-${i}`,
      span: Math.min(7, days.length - i),
      label: `W${isoWeekNumber(start)} · ${fmtDM(start)}–${fmtDM(end)}`,
    })
  }

  const DAY_W = 56
  // Widened so long task/subtask names aren't truncated — matches the wider
  // Name column on the List view. The left pane is fixed-width; the timeline
  // scrolls independently, so this only trades a little timeline viewport.
  const NAME_W = 360
  const DUE_W = 130
  const ROW_H = 36
  const HEADER_H = 56 // week strip + day cells stacked
  const todayIso = today.toISOString().slice(0, 10)

  function xForDateTime(iso) {
    const d = new Date(iso)
    return ((d.getTime() - min.getTime()) / DAY_MS) * DAY_W
  }
  function xForToday() {
    return ((today.getTime() - min.getTime()) / DAY_MS) * DAY_W
  }
  // Centre today's column in the timeline viewport — backs the "Today"
  // jump button so the user can return to the current date from
  // anywhere on the (±1-year) canvas.
  function scrollToToday() {
    const el = timelineRef.current
    if (!el) return
    // Suppress the infinite-scroll edge-extend for the length of this
    // programmatic tween. A Today jump that BEGINS near the left edge would
    // otherwise trip a past-extend mid-scroll, shifting `min` and offsetting
    // the target so the jump landed short — which is why it needed a second
    // click. `onTimelineScroll` early-returns while `extendingRef` is set.
    extendingRef.current = true
    // Fixed-duration rAF tween — smooth at any distance (native smooth
    // scroll stutters across this wide canvas).
    smoothScrollLeftTo(el, xForToday() + DAY_W / 2 - el.clientWidth / 2)
    window.setTimeout(() => {
      extendingRef.current = false
    }, 500) // just past the 450 ms tween
  }

  // Publish the live scroll-to-today handle to the parent toolbar's "Today"
  // button. No dep array on purpose: `scrollToToday` closes over the live date
  // range, so re-publishing each render keeps the handle fresh; cleared on
  // unmount. In the empty state below, scrollToToday safely no-ops (timelineRef
  // is null).
  useEffect(() => {
    if (!todayHandleRef) return undefined
    todayHandleRef.current = scrollToToday
    return () => {
      todayHandleRef.current = null
    }
  })

  // No dated tasks → empty state. Placed AFTER every hook above so hook order
  // is identical whether or not the Gantt has data — fixes the Rules-of-Hooks
  // violation where this early return sat before the "Today" effect and skipped
  // it when the task list was empty (DEF-2).
  if (dueTasks.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center text-sm text-[color:var(--color-ink-muted)]">
        Tasks need a due date to appear on the Gantt. Set due dates from the
        List view inside each Space.
      </div>
    )
  }

  // Visible task list — collapsed groups contribute only the header.
  // For each open group: render every TOP-LEVEL task, then (if that
  // parent is expanded) its subtasks indented under it. Subtasks
  // without a corresponding parent (e.g. parent filtered out) get
  // surfaced as orphans at the bottom of the group so they never
  // disappear silently.
  const visibleRows = []
  for (const g of groups) {
    visibleRows.push({ kind: 'group', group: g })
    if (!collapsedGroups.has(g.key)) {
      const consumedSubIds = new Set()
      for (const t of g.parents) {
        const subs = g.subtasksByParent.get(String(t.id)) ?? []
        visibleRows.push({
          kind: 'task',
          task: t,
          group: g,
          depth: 0,
          subCount: subs.length,
          expanded: expandedParents.has(t.id),
        })
        if (subs.length > 0 && expandedParents.has(t.id)) {
          for (const sub of subs) {
            consumedSubIds.add(String(sub.id))
            visibleRows.push({
              kind: 'task',
              task: sub,
              group: g,
              depth: 1,
              subCount: 0,
              expanded: false,
            })
          }
        } else {
          // Even when collapsed, mark these subtasks as consumed so
          // the orphan pass below doesn't double-render them.
          for (const sub of subs) consumedSubIds.add(String(sub.id))
        }
      }
      // Orphans — subtasks whose parent isn't in this group's
      // top-level list (e.g. parent had no dueDate and was filtered
      // out upstream). Render them at depth 0 so users still see them.
      for (const [parentKey, subs] of g.subtasksByParent.entries()) {
        for (const sub of subs) {
          if (consumedSubIds.has(String(sub.id))) continue
          visibleRows.push({
            kind: 'task',
            task: sub,
            group: g,
            depth: 0,
            subCount: 0,
            expanded: false,
          })
        }
        // ESLint: `parentKey` deliberately unused — we iterate values.
        void parentKey
      }
    }
  }

  const TIMELINE_W = days.length * DAY_W

  return (
    <>
      {hiddenCount > 0 ? (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-4 py-2.5 text-sm text-[color:var(--color-ink-muted)]">
          <GanttChart
            size={15}
            className="mt-0.5 shrink-0 text-[color:var(--color-ink-subtle)]"
          />
          <span>
            {hiddenCount} {hiddenCount === 1 ? 'task is' : 'tasks are'} hidden
            because {hiddenCount === 1 ? 'it has' : 'they have'} no due date.
            Set a due date from the List view inside each Space to place{' '}
            {hiddenCount === 1 ? 'it' : 'them'} on the timeline.
          </span>
        </div>
      ) : null}
      <div className="relative mt-4 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] flex overflow-hidden">
      {/* NOTE: the "Jump to today" button used to float here, absolutely
       *  positioned over the header. It kept covering header text (QA): the
       *  timeline header scrolls beneath a button pinned to the card, so a
       *  week label or a month band's label would land under it depending on
       *  the visible range — no offset or size could fix that. It now lives in
       *  the view-tabs toolbar next to Filter (see `todayHandleRef`), matching
       *  the Space Gantt and the Task Monitor Gantt. */}
      {/* ── Left pane: Name + Due Date ─────────────────────────────── */}
      <div
        className="shrink-0 border-r border-[color:var(--color-rule)] bg-[color:var(--color-card)]"
        style={{ width: NAME_W + DUE_W }}
      >
        <div
          className="flex items-end bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-sm font-medium text-[color:var(--color-ink-muted)]"
          style={{ height: HEADER_H }}
        >
          <div className="px-3 py-2" style={{ width: NAME_W }}>
            Task Name
          </div>
          <div className="px-3 py-2" style={{ width: DUE_W }}>
            Due Date
          </div>
        </div>
        {visibleRows.map((row) => {
          if (row.kind === 'group') {
            const collapsed = collapsedGroups.has(row.group.key)
            return (
              <div
                key={`name-${row.group.key}`}
                className="flex items-center bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
                style={{ height: ROW_H }}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(row.group.key)}
                  className="flex items-center gap-1.5 px-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] h-full"
                  style={{ width: NAME_W }}
                  aria-expanded={!collapsed}
                  title={collapsed ? 'Expand list' : 'Collapse list'}
                >
                  <ChevronRight
                    size={14}
                    className={
                      'shrink-0 text-[color:var(--color-ink-muted)] transition-transform ' +
                      (collapsed ? '' : 'rotate-90')
                    }
                  />
                  <LayoutList
                    size={13}
                    className="shrink-0 text-[color:var(--color-ink-muted)]"
                  />
                  <span className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                    {row.group.listName}
                  </span>
                  <span className="text-xs text-[color:var(--color-ink-muted)] ml-1">
                    · {row.group.spaceName}
                  </span>
                  <span className="ml-auto text-xs text-[color:var(--color-ink-subtle)] tabular-nums">
                    {row.group.tasks.length}
                  </span>
                </button>
                <div
                  className="px-3 text-xs text-[color:var(--color-ink-subtle)]"
                  style={{ width: DUE_W }}
                />
              </div>
            )
          }
          const t = row.task
          const depth = row.depth ?? 0
          const subCount = row.subCount ?? 0
          const expanded = row.expanded ?? false
          const hasSubs = subCount > 0
          const color = statusColors.forTask(t)
          // Indent subtasks under their parent. Depth 0 = 32px
          // (existing pl-8 baseline so parents line up with the
          // group header's chevron). Depth 1 = 32 + 24 = 56px.
          const leftPad = 32 + depth * 24
          return (
            <div
              key={`name-${t.id}`}
              className="flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
              style={{ height: ROW_H }}
              title={`${t.name} — Due ${formatDateTime(t.dueDate)}`}
            >
              <div
                className="flex items-center gap-2 px-3"
                style={{ width: NAME_W, paddingLeft: leftPad }}
              >
                {hasSubs ? (
                  <button
                    type="button"
                    onClick={() => toggleParent(t.id)}
                    aria-expanded={expanded}
                    title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
                    className="p-0.5 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]"
                  >
                    <ChevronRight
                      size={12}
                      className={
                        'shrink-0 transition-transform ' +
                        (expanded ? 'rotate-90' : '')
                      }
                    />
                  </button>
                ) : (
                  // Spacer so parent rows without subtasks line up
                  // with parents that DO have a chevron.
                  <span className="w-3 shrink-0" aria-hidden="true" />
                )}
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <Link
                  // Open the task's detail panel directly (DF_19 / bug 16553).
                  to={`/app/team-tasks?task=${t.id}`}
                  className="text-sm text-[color:var(--color-ink-strong)] truncate hover:underline cursor-pointer"
                >
                  {t.name}
                </Link>
                {hasSubs ? (
                  <span className="text-[10px] tabular-nums text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] rounded-full px-1.5 py-0.5 shrink-0">
                    {subCount}
                  </span>
                ) : null}
              </div>
              <div
                className="px-3 text-xs text-[color:var(--color-ink-muted)] tabular-nums inline-flex items-center gap-1"
                style={{ width: DUE_W }}
              >
                <Calendar
                  size={12}
                  className="text-[color:var(--color-ink-muted)] shrink-0"
                />
                <span className="truncate">
                  {formatDateTime(t.dueDate)}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Right pane: timeline ───────────────────────────────────── */}
      {/* Native scrollbar hidden — the visible scrollbar is rendered
       *  by `<StickyTimelineScrollbar>` below, pinned to the viewport
       *  bottom so it's always reachable no matter how far the user
       *  has scrolled vertically. Same pattern as the Spaces page.
       *  `onScroll` drives the infinite-scroll edge detection: when
       *  the viewport gets within EDGE_DAYS of either edge, we bump
       *  `pastDays`/`futureDays` by another year so the canvas
       *  visibly grows in that direction. */}
      <div
        ref={timelineRef}
        onScroll={onTimelineScroll}
        className="flex-1 overflow-x-auto view-hscroll-sticky"
      >
        <div style={{ minWidth: TIMELINE_W, position: 'relative' }}>
          {/* Header — week strip + day cells stacked */}
          <div
            className="bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] sticky top-0 z-10"
            style={{ height: HEADER_H }}
          >
            <div className="flex" style={{ height: HEADER_H / 2 }}>
              {weeks.map((w) => (
                <div
                  key={w.key}
                  className="shrink-0 flex items-center justify-center px-2 text-[12px] font-medium text-[color:var(--color-ink-muted)] border-r border-[color:var(--color-rule)] truncate"
                  style={{ width: w.span * DAY_W }}
                  title={w.label}
                >
                  {w.label}
                </div>
              ))}
            </div>
            <div className="flex" style={{ height: HEADER_H / 2 }}>
              {days.map((d) => {
                const iso = d.toISOString().slice(0, 10)
                const isToday = iso === todayIso
                return (
                  <div
                    key={iso}
                    className={
                      'shrink-0 flex items-center justify-center gap-1 text-[12px] border-r border-[color:var(--color-rule)] ' +
                      (isToday
                        ? 'font-medium text-[color:var(--color-accent)]'
                        : 'text-[color:var(--color-ink-muted)]')
                    }
                    style={{ width: DAY_W }}
                  >
                    <span className="leading-none">
                      {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                    </span>
                    {isToday ? (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--color-priority-urgent)] text-white text-[10px] font-medium tabular-nums">
                        {d.getDate()}
                      </span>
                    ) : (
                      <span className="tabular-nums">{d.getDate()}</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Body rows — same order as the left pane so they line up */}
          <div className="relative">
            {/* Vertical today line spans the whole body */}
            <div
              className="absolute top-0 bottom-0 w-px bg-[color:var(--color-priority-urgent)] z-[1] pointer-events-none"
              style={{ left: xForToday() + DAY_W / 2 }}
              aria-hidden="true"
            />

            {visibleRows.map((row) => {
              if (row.kind === 'group') {
                return (
                  <div
                    key={`tl-${row.group.key}`}
                    className="bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)]"
                    style={{ height: ROW_H }}
                  />
                )
              }
              const t = row.task
              // Visual match for the Spaces page Gantt: render a
              // full-width SPAN bar from startDate → dueDate
              // (inclusive end day), coloured by priority (urgent
              // red / high orange / normal blue / low gray). When
              // the task has no startDate, treat the dueDate as a
              // 1-day span. The task name floats inside the bar in
              // white when there's enough width to fit it (> 60px),
              // matching the SpacePage Gantt's read.
              const color = statusColors.forTask(t)
              const barColor = t.priority ? PRIORITY_COLOR[t.priority] : color
              // Use startDate if present; otherwise fall back to
              // dueDate so the bar still renders as a single-day cell.
              const startIso = t.startDate ?? t.dueDate
              const endIso = t.dueDate ?? t.startDate
              const startD = startIso
                ? startOfDay(new Date(startIso))
                : null
              const endD = endIso ? startOfDay(new Date(endIso)) : null
              const isOverdue =
                t.dueDate &&
                endD &&
                endD < today &&
                t.status !== 'complete' &&
                t.status !== 'completed' &&
                t.status !== 'done'
              const barLeft = startD ? xForDateTime(startD.toISOString()) : 0
              // `+ DAY_W` so the end day is INCLUSIVE — a task due
              // on May 15 fills all of May 15's column.
              const barRight = endD
                ? xForDateTime(endD.toISOString()) + DAY_W
                : DAY_W
              const barWidth = Math.max(barRight - barLeft, 16)
              const showLeftChevron =
                viewport.clientWidth > 0 && barRight <= viewport.scrollLeft
              const showRightChevron =
                viewport.clientWidth > 0 &&
                barLeft >= viewport.scrollLeft + viewport.clientWidth
              return (
                <div
                  key={`tl-${t.id}`}
                  className="relative flex items-center border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]"
                  style={{ height: ROW_H }}
                >
                  {/* Sticky-left off-screen indicator chevron — its
                   *  natural flex position is the row's left edge, so
                   *  CSS sticky pins it to the viewport's left edge as
                   *  the user scrolls right. No JS updates per scroll
                   *  tick: the browser handles the pinning. */}
                  {showLeftChevron ? (
                    <button
                      type="button"
                      onClick={() => {
                        timelineRef.current?.scrollTo({
                          left: Math.max(0, barLeft - 40),
                          behavior: 'smooth',
                        })
                      }}
                      title={`Jump to ${t.name}`}
                      aria-label={`Jump to ${t.name}`}
                      className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 ml-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                      style={{ left: 6 }}
                    >
                      <ChevronLeft size={14} />
                    </button>
                  ) : null}
                  {/* Flex spacer so the right chevron docks at the
                   *  flex line's far edge, where sticky-right can
                   *  pin it to the viewport's right edge. */}
                  <div className="flex-1" />
                  {showRightChevron ? (
                    <button
                      type="button"
                      onClick={() => {
                        timelineRef.current?.scrollTo({
                          left: Math.max(0, barLeft - 40),
                          behavior: 'smooth',
                        })
                      }}
                      title={`Jump to ${t.name}`}
                      aria-label={`Jump to ${t.name}`}
                      className="sticky shrink-0 z-10 flex items-center justify-center h-6 w-6 mr-1.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink-strong)] cursor-pointer shadow-sm"
                      style={{ right: 6 }}
                    >
                      <ChevronRight size={14} />
                    </button>
                  ) : null}
                  {/* Priority-coloured span bar — absolutely
                   *  positioned, so the flex chevrons above never
                   *  push it sideways. */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 rounded-md flex items-center px-2 select-none"
                    style={{
                      left: barLeft,
                      width: barWidth,
                      height: ROW_H - 12,
                      backgroundColor: barColor,
                      opacity: 0.9,
                    }}
                    title={`${t.name} — Due ${formatDateTime(t.dueDate)}${isOverdue ? ' · Overdue' : ''}`}
                    aria-label={`${t.name} due ${formatDateTime(t.dueDate)}`}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Sticky horizontal scrollbar pinned to the viewport bottom —
       *  mirrors the Spaces page Gantt's pattern. Native scrollbar
       *  on the timeline above is hidden; this is the visible one. */}
      <StickyTimelineScrollbar
        timelineRef={timelineRef}
        timelineWidth={TIMELINE_W}
      />
      </div>
    </>
  )
}


// ---------- Add Task ----------

// Intentionally parked (see the mount-removed note above) until the
// assign-and-create backend flow lands; kept so it is easy to re-wire.
// eslint-disable-next-line no-unused-vars
function CreateTeamTaskDialog({ onClose }) {
  const qc = useQueryClient()
  // Form dialog — Escape closes, backdrop click does NOT (matches the
  // shared Modal's dismissal model; see SupportPage.ComposeDialog).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // Multi-assign: a Set of selected teammate ids. A task can be handed
  // to several teammates at once — each gets their own Inbox brief.
  const [assigneeIds, setAssigneeIds] = useState(() => new Set())

  function toggleAssignee(id) {
    setAssigneeIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Department membership lives on workspace_members, so /users/teammates
  // needs a workspace context. The Add Task dialog has no per-call
  // workspace, so we default to the actor's first workspace — same scope
  // the picker has always implicitly assumed.
  const myWorkspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return workspaceListResponse.parse(res.data)
    },
  })
  const primaryWorkspaceId = myWorkspacesQuery.data?.data?.[0]?.id ?? null

  // Same-department teammates the actor can delegate to. The API already
  // excludes super_admin and the actor themselves.
  const teammatesQuery = useQuery({
    queryKey: ['teammates', primaryWorkspaceId],
    queryFn: async () => {
      const res = await api.get('/users/teammates', {
        params: { workspaceId: primaryWorkspaceId },
      })
      return teammatesResponse.parse(res.data)
    },
    enabled: Boolean(primaryWorkspaceId),
  })

  // Delegation candidates are whoever the teammates endpoint returns —
  // it's already scoped to the actor's workspace and excludes
  // super_admin + the actor themselves. There's no role-tier ladder
  // any more: a user who can reach this page (they hold a delegation /
  // team-visibility operation) may assign to any teammate in scope.
  const candidates = teammatesQuery.data?.data ?? []

  const createMutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim()
      const trimmedDesc = description.trim()
      if (trimmedName.length < 2)
        throw new Error('Task Name must be at least 2 characters.')
      if (assigneeIds.size === 0)
        throw new Error('Pick at least one teammate to assign.')
      const body = { name: trimmedName, assigneeIds: [...assigneeIds] }
      if (trimmedDesc.length > 0) body.description = trimmedDesc
      const res = await api.post('/team/tasks', body)
      return res.data
    },
    onSuccess: (result) => {
      const n = Number(result?.count ?? assigneeIds.size)
      toast.success(
        n === 1 ? 'Task assigned.' : `Task assigned to ${n} teammates.`,
      )
      void qc.invalidateQueries({ queryKey: ['team', 'tasks'] })
      onClose()
    },
    onError: (err) => {
      // Plan-cap hit (maxTasks): the global API interceptor already shows the
      // single amber "upgrade your plan" warning, so suppress our own toast.
      if (isPlanLimitError(err)) return
      const apiMsg = extractApiError(err).message
      toast.error(apiMsg !== 'Something went wrong.' ? apiMsg : (err ).message)
    },
  })

  const departmentLabel = teammatesQuery.data?.departmentName ?? null

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="w-[520px] max-h-[90vh] flex flex-col rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <p className="font-medium text-base text-[color:var(--color-ink-strong)] inline-flex items-center gap-2">
            <CheckSquare size={16} /> Assign a new task
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] mb-1">
              Task Name<span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a Task Name"
              className={
                'w-full h-10 px-3 rounded-md border bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] ' +
                (lengthError(name, undefined, 200, 'Task Name')
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
            />
            {lengthError(name, undefined, 200, 'Task Name') ? (
              <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {lengthError(name, undefined, 200, 'Task Name')}
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] mb-1">
              Task Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Enter a Task Description"
              className={
                'w-full px-3 py-2 rounded-md border bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] resize-y max-h-[40vh] leading-snug ' +
                (description.length > 5000
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
            />
            <p
              className={
                'mt-0.5 text-sm text-right ' +
                (description.length > 5000
                  ? 'text-[color:var(--color-priority-urgent)] font-semibold'
                  : 'text-[color:var(--color-ink-subtle)]')
              }
            >
              {description.length}/5000
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] mb-1">
              Assign Task to Members<span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
              {assigneeIds.size > 0 ? (
                <span className="ml-1 font-normal normal-case tracking-normal text-[color:var(--color-ink-muted)]">
                  ({assigneeIds.size} selected)
                </span>
              ) : null}
            </label>
            {teammatesQuery.isLoading ? (
              <p className="text-xs text-[color:var(--color-ink-muted)] py-2">
                Loading teammates…
              </p>
            ) : candidates.length === 0 ? (
              <p className="text-xs text-[color:var(--color-ink-muted)] py-2">
                No teammates in your department to assign tasks to yet.
              </p>
            ) : (
              <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] max-h-52 overflow-y-auto divide-y divide-[color:var(--color-rule)]">
                {candidates.map((m) => {
                  const checked = assigneeIds.has(m.id)
                  return (
                    <label
                      key={m.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssignee(m.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                          {m.fullName}
                        </p>
                        <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
                          {m.emailId}
                        </p>
                      </div>
                      <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] border border-[color:var(--color-accent-border)] shrink-0">
                        {workRoleShort(m.workRole)}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
            {departmentLabel ? (
              <p className="mt-1 text-sm text-[color:var(--color-ink-subtle)]">
                Showing teammates in {departmentLabel}.
              </p>
            ) : null}
          </div>
          <p className="text-sm text-[color:var(--color-ink-muted)] leading-snug">
            The task lands in each selected teammate's personal Space, where
            they can pick an SOP or break it into subtasks themselves. You'll
            see progress here on Team Tasks.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-sm border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => createMutation.mutate()}
            disabled={
              createMutation.isPending ||
              name.trim().length < 2 ||
              assigneeIds.size === 0
            }
            className="px-3 py-1.5 rounded text-sm bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? 'Assigning…' : 'Assign task'}
          </button>
        </div>
      </div>
    </div>
  )
}

function workRoleShort(role){
  if (role === 'manager') return 'Manager'
  if (role === 'team_leader') return 'Team Leader'
  return 'Employee'
}
